import { toGuaranteeStatus, toInvoiceStatus } from './status'

declare global {
    interface Window {
        shopify?: {
            idToken: () => Promise<string>
            query: (query: string, options?: { variables?: Record<string, unknown> }) => Promise<any>
            resourcePicker: (options: { type: string; multiple?: boolean; action?: string; selectionIds?: any[] }) => Promise<any[] | undefined>
        }
    }
}

const DRAFT_ORDER_QUERY = `
query DraftOrder($id: ID!) {
  draftOrder(id: $id) {
    id
    name
    status
    totalPriceSet { shopMoney { amount currencyCode } }
    subtotalPriceSet { shopMoney { amount currencyCode } }
    totalTaxSet { shopMoney { amount currencyCode } }
    totalDiscountsSet { shopMoney { amount currencyCode } }
    totalShippingPriceSet { shopMoney { amount currencyCode } }
    appliedDiscount { title description value valueType }
    shippingLine { title originalPriceSet { shopMoney { amount currencyCode } } }
    taxesIncluded
    taxExempt
    email
    phone
    lineItems(first: 50) {
      nodes {
        id
        title
        quantity
        originalUnitPriceSet { shopMoney { amount } }
        product { id }
        variant { id title }
        taxLines { rate priceSet { shopMoney { amount } } }
      }
    }
    shippingAddress {
      address1 address2 city province zip countryCode phone
      firstName lastName company
    }
    billingAddress {
      address1 address2 city province zip countryCode phone
      firstName lastName company
    }
    customer { id email phone firstName lastName }
    purchasingEntity {
      ... on PurchasingCompany {
        company { id name }
        location {
          id name phone
          taxSettings { taxRegistrationId }
          billingAddress { firstName lastName phone companyName }
          shippingAddress { firstName lastName phone companyName }
        }
        contact { id customer { id firstName lastName phone email } }
      }
    }
    metafield(namespace: "$app:bnplx", key: "payment") { id value }
  }
}
`

const METAFIELD_SET_MUTATION = `
mutation MetafieldSet($metafields: [MetafieldsSetInput!]!) {
  metafieldsSet(metafields: $metafields) {
    metafields { id }
    userErrors { field message }
  }
}
`

const DRAFT_ORDER_CUSTOM_ATTRS_QUERY = `
query DraftOrderCustomAttrs($id: ID!) {
  draftOrder(id: $id) {
    customAttributes { key value }
  }
}
`

const DRAFT_ORDER_UPDATE_MUTATION = `
mutation DraftOrderUpdate($id: ID!, $input: DraftOrderInput!) {
  draftOrderUpdate(id: $id, input: $input) {
    draftOrder { id }
    userErrors { field message }
  }
}
`

const DRAFT_ORDER_COMPLETE_MUTATION = `
mutation DraftOrderComplete($id: ID!) {
  draftOrderComplete(id: $id) {
    draftOrder {
      order { id legacyResourceId name }
    }
    userErrors { field message }
  }
}
`

const DRAFT_ORDERS_LIST_QUERY = `
query DraftOrders($first: Int, $after: String, $last: Int, $before: String) {
  draftOrders(first: $first, after: $after, last: $last, before: $before, reverse: true) {
    edges {
      node {
        id
        name
        status
        totalPriceSet { shopMoney { amount currencyCode } }
        customer { id firstName lastName }
        purchasingEntity {
          ... on PurchasingCompany {
            company { name }
          }
        }
        metafield(namespace: "$app:bnplx", key: "payment") { value }
      }
    }
    pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
  }
}
`

const PRODUCTS_SEARCH_QUERY = `
query Products($query: String!) {
  products(first: 10, query: $query) {
    edges {
      node {
        id
        title
        featuredImage { url altText }
        variants(first: 20) {
          edges {
            node {
              id
              title
              price
              inventoryQuantity
            }
          }
        }
      }
    }
  }
}
`

const CUSTOMERS_SEARCH_QUERY = `
query Customers($query: String) {
  customers(first: 25, query: $query, sortKey: NAME) {
    edges {
      node {
        id
        firstName
        lastName
        email
        phone
        numberOfOrders
        defaultAddress { address1 address2 city province zip countryCode phone firstName lastName }
        companyContactProfiles {
          id
          company {
            id
            name
            locations(first: 5) {
              edges {
                node {
                  id
                  name
                  shippingAddress { address1 address2 city province zip countryCode phone firstName lastName companyName }
                  billingAddress { address1 address2 city province zip countryCode phone firstName lastName companyName }
                  taxExemptions
                }
              }
            }
          }
        }
      }
    }
  }
}
`

const DRAFT_ORDER_CREATE_MUTATION = `
mutation DraftOrderCreate($input: DraftOrderInput!) {
  draftOrderCreate(input: $input) {
    draftOrder { id legacyResourceId name }
    userErrors { field message }
  }
}
`

export type DraftOrderAddress = {
    address1: string | null
    address2: string | null
    city: string | null
    province: string | null
    zip: string | null
    countryCode: string | null
    phone: string | null
    firstName: string | null
    lastName: string | null
    company: string | null
}

export type DraftOrderLineItem = {
    id: string
    title: string
    quantity: number
    originalUnitPriceSet: { shopMoney: { amount: string } }
    product: { id: string } | null
    variant: { id: string; title: string } | null
    taxLines: Array<{ rate: number; priceSet: { shopMoney: { amount: string } } }>
}

export type PurchasingCompany = {
    company: { id: string; name: string }
    location: {
        id: string
        name: string | null
        phone: string | null
        taxSettings: { taxRegistrationId: string | null } | null
        billingAddress: { firstName: string | null; lastName: string | null; phone: string | null; companyName: string | null } | null
        shippingAddress: { firstName: string | null; lastName: string | null; phone: string | null; companyName: string | null } | null
    }
    contact: {
        id: string
        customer: {
            id: string
            firstName: string | null
            lastName: string | null
            email: string | null
            phone: string | null
        }
    } | null
}

export type DraftOrder = {
    id: string
    name: string
    status: string
    totalPriceSet: { shopMoney: { amount: string; currencyCode: string } }
    subtotalPriceSet: { shopMoney: { amount: string; currencyCode: string } }
    totalTaxSet: { shopMoney: { amount: string; currencyCode: string } }
    totalDiscountsSet: { shopMoney: { amount: string; currencyCode: string } } | null
    totalShippingPriceSet: { shopMoney: { amount: string; currencyCode: string } } | null
    appliedDiscount: { title: string; description: string; value: number; valueType: string } | null
    shippingLine: { title: string; originalPriceSet: { shopMoney: { amount: string; currencyCode: string } } } | null
    taxesIncluded: boolean
    taxExempt: boolean
    email: string | null
    phone: string | null
    lineItems: { nodes: DraftOrderLineItem[] }
    shippingAddress: DraftOrderAddress | null
    billingAddress: DraftOrderAddress | null
    customer: { id: string; email: string | null; phone: string | null; firstName: string | null; lastName: string | null } | null
    purchasingEntity: PurchasingCompany | null
    metafield: { id: string; value: string } | null
}

export type PaymentMetafield = {
    paymentId: string
    status: string
    amount: string
    currency: string
    captureId?: string
    orderId?: string
    orderName?: string
    // Human-readable reason for non-connector outcomes (incomplete: missing
    // fields; error: generic). Not shown for genuine connector declines.
    error?: string
}

async function waitForAppBridge(timeout = 10000): Promise<void> {
    if (window.shopify?.idToken) return
    const start = Date.now()
    while (Date.now() - start < timeout) {
        await new Promise(r => setTimeout(r, 200))
        if (window.shopify?.idToken) return
    }
}

async function shopifyQuery(query: string, variables?: Record<string, unknown>): Promise<any> {
    // Route GraphQL through our backend which proxies to Shopify Admin API.
    // The backend uses the session token to authenticate with Shopify.
    await waitForAppBridge()
    const token = await window.shopify?.idToken?.()
    const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
    })
    if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`GraphQL request failed (${res.status}): ${text}`)
    }
    return await res.json()
}

export async function fetchDraftOrder(draftOrderId: string): Promise<DraftOrder> {
    const gid = `gid://shopify/DraftOrder/${draftOrderId}`
    const result = await shopifyQuery(DRAFT_ORDER_QUERY, { id: gid })
    if (result.errors) throw new Error(result.errors[0]?.message || 'GraphQL error')
    return result.data.draftOrder
}

export async function writePaymentMetafield(
    draftOrderGid: string,
    payment: PaymentMetafield
): Promise<void> {
    const result = await shopifyQuery(METAFIELD_SET_MUTATION, {
        metafields: [{
            ownerId: draftOrderGid,
            namespace: '$app:bnplx',
            key: 'payment',
            type: 'json',
            value: JSON.stringify(payment),
        }],
    })
    const errors = result.data?.metafieldsSet?.userErrors
    if (errors?.length) throw new Error(errors[0].message)
}

// Custom-attribute keys planted on the draft order. Unlike the $app: metafield
// (private to this app), custom attributes are Admin-API readable and survive
// the draft→order conversion — so external systems (e.g. BC) can read them.
// - BNPLX_PAYMENT_ATTR: order↔payment correlation. Must match BNPLX_PAYMENT_ATTR
//   in server/src/routes/webhooks.js.
// - BNPLX_STATUS_ATTR: the guarantee status, for external ship/no-ship gating.
export const BNPLX_PAYMENT_ATTR = 'bnplx_payment_id'
export const BNPLX_STATUS_ATTR = 'bnplx_guarantee_status'
export const BNPLX_INVOICE_ATTR = 'bnplx_invoice_status'

/**
 * Merge BNPLX custom attributes onto the draft order. Pass only the keys you
 * want to set. draftOrderUpdate replaces customAttributes wholesale, so we
 * fetch the current ones and merge to avoid clobbering attributes set by other
 * systems. IMPORTANT: await calls sequentially — concurrent calls race on the
 * read-then-write merge and clobber each other.
 */
export async function writeBnplxAttributes(
    draftOrderGid: string,
    attrs: { paymentId?: string; status?: string | null },
): Promise<void> {
    const setKeys: { key: string; value: string }[] = []
    if (attrs.paymentId !== undefined) setKeys.push({ key: BNPLX_PAYMENT_ATTR, value: attrs.paymentId })
    if (attrs.status !== undefined) {
        // Only the guarantee status belongs on the DRAFT. A draft is never invoiced
        // (invoicing = completing it into an order), so bnplx_invoice_status here is
        // always not_invoiced — noise that misleads the merchant. It lives on the
        // ORDER instead (see writeOrderBnplxAttributes).
        setKeys.push({ key: BNPLX_STATUS_ATTR, value: toGuaranteeStatus(attrs.status) })
    }
    if (setKeys.length === 0) return

    const current = await shopifyQuery(DRAFT_ORDER_CUSTOM_ATTRS_QUERY, { id: draftOrderGid })
    const existing: { key: string; value: string }[] =
        current.data?.draftOrder?.customAttributes ?? []

    const overwritten = new Set(setKeys.map((a) => a.key))
    overwritten.add(BNPLX_INVOICE_ATTR) // strip any stale invoice status left on the draft
    const merged = [
        ...existing.filter((a) => !overwritten.has(a.key)).map((a) => ({ key: a.key, value: a.value })),
        ...setKeys,
    ]

    const result = await shopifyQuery(DRAFT_ORDER_UPDATE_MUTATION, {
        id: draftOrderGid,
        input: { customAttributes: merged },
    })
    const errors = result.data?.draftOrderUpdate?.userErrors
    if (errors?.length) throw new Error(errors[0].message)
}

/** Plant the BNPLX payment id + status together in one merge (order correlation
 * + external ship gate). Used at guarantee creation. */
export async function writePaymentNoteAttribute(
    draftOrderGid: string,
    paymentId: string,
    status?: string | null,
): Promise<void> {
    await writeBnplxAttributes(draftOrderGid, { paymentId, status })
}

const ORDER_CUSTOM_ATTRS_QUERY = `
query OrderCustomAttrs($id: ID!) {
  order(id: $id) { customAttributes { key value } }
}
`

const ORDER_UPDATE_MUTATION = `
mutation OrderUpdate($input: OrderInput!) {
  orderUpdate(input: $input) { userErrors { field message } }
}
`

/**
 * Set BNPLX custom attributes on a completed ORDER. At completion the draft's
 * attributes are frozen onto the order, and a completed draft can no longer be
 * updated — so after capture we update the order directly. This is what external
 * systems (e.g. BC) read for ship/no-ship + invoice sync. Same merge semantics as
 * writeBnplxAttributes; call sequentially.
 */
export async function writeOrderBnplxAttributes(
    orderGid: string,
    attrs: { paymentId?: string; status?: string | null },
): Promise<void> {
    const setKeys: { key: string; value: string }[] = []
    if (attrs.paymentId !== undefined) setKeys.push({ key: BNPLX_PAYMENT_ATTR, value: attrs.paymentId })
    if (attrs.status !== undefined) {
        setKeys.push({ key: BNPLX_STATUS_ATTR, value: toGuaranteeStatus(attrs.status) })
        setKeys.push({ key: BNPLX_INVOICE_ATTR, value: toInvoiceStatus(attrs.status) })
    }
    if (setKeys.length === 0) return

    const current = await shopifyQuery(ORDER_CUSTOM_ATTRS_QUERY, { id: orderGid })
    const existing: { key: string; value: string }[] = current.data?.order?.customAttributes ?? []
    const overwritten = new Set(setKeys.map((a) => a.key))
    const merged = [
        ...existing.filter((a) => !overwritten.has(a.key)).map((a) => ({ key: a.key, value: a.value })),
        ...setKeys,
    ]
    const result = await shopifyQuery(ORDER_UPDATE_MUTATION, { input: { id: orderGid, customAttributes: merged } })
    const errors = result.data?.orderUpdate?.userErrors
    if (errors?.length) throw new Error(errors[0].message)
}

export async function completeDraftOrder(
    draftOrderGid: string
): Promise<{ orderId: string; legacyResourceId: string; orderName: string }> {
    const result = await shopifyQuery(DRAFT_ORDER_COMPLETE_MUTATION, { id: draftOrderGid })
    const errors = result.data?.draftOrderComplete?.userErrors
    if (errors?.length) throw new Error(errors[0].message)
    const order = result.data.draftOrderComplete.draftOrder.order
    return { orderId: order.id, legacyResourceId: order.legacyResourceId, orderName: order.name }
}

export type DraftOrderListItem = {
    id: string
    name: string
    status: string
    totalPriceSet: { shopMoney: { amount: string; currencyCode: string } }
    customer: { id: string; firstName: string | null; lastName: string | null } | null
    purchasingEntity: { company: { name: string } } | null
    metafield: { value: string } | null
}

export type ProductVariant = {
    id: string
    title: string
    price: string
    inventoryQuantity: number
}

export type ProductSearchResult = {
    id: string
    title: string
    featuredImage: { url: string; altText: string | null } | null
    variants: { edges: Array<{ node: ProductVariant }> }
}

export type CompanyLocationAddress = {
    address1: string | null
    address2: string | null
    city: string | null
    province: string | null
    zip: string | null
    countryCode: string | null
    phone: string | null
    firstName: string | null
    lastName: string | null
    companyName: string | null
}

export type CompanyLocation = {
    id: string
    name: string | null
    shippingAddress: CompanyLocationAddress | null
    billingAddress: CompanyLocationAddress | null
    taxExemptions: string[]
}

export type CompanyContactProfile = {
    id: string
    company: {
        id: string
        name: string
        locations: { edges: Array<{ node: CompanyLocation }> }
    }
}

export type CustomerSearchResult = {
    id: string
    firstName: string | null
    lastName: string | null
    email: string | null
    phone: string | null
    numberOfOrders: string
    defaultAddress: DraftOrderAddress | null
    companyContactProfiles: CompanyContactProfile[]
}

export type PageInfo = {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor: string | null
    endCursor: string | null
}

export async function fetchDraftOrders(
    params: { first?: number; after?: string; last?: number; before?: string } = { first: 25 }
): Promise<{ items: DraftOrderListItem[]; pageInfo: PageInfo }> {
    const result = await shopifyQuery(DRAFT_ORDERS_LIST_QUERY, params)
    if (result.errors) throw new Error(result.errors[0]?.message || 'GraphQL error')
    const edges = result.data.draftOrders.edges
    return {
        items: edges.map((e: any) => e.node),
        pageInfo: result.data.draftOrders.pageInfo,
    }
}

export async function searchProducts(query: string): Promise<ProductSearchResult[]> {
    const result = await shopifyQuery(PRODUCTS_SEARCH_QUERY, { query })
    if (result.errors) throw new Error(result.errors[0]?.message || 'GraphQL error')
    return result.data.products.edges.map((e: any) => e.node)
}

export async function searchCustomers(query?: string): Promise<CustomerSearchResult[]> {
    const variables: any = {}
    if (query && query.trim()) variables.query = query
    const result = await shopifyQuery(CUSTOMERS_SEARCH_QUERY, variables)
    if (result.errors) throw new Error(result.errors[0]?.message || 'GraphQL error')
    return result.data.customers.edges.map((e: any) => e.node)
}

export type DraftOrderCreateInput = {
    customerId?: string | null
    lineItems: Array<{ variantId: string; quantity: number }>
    shippingAddress?: {
        firstName?: string; lastName?: string; company?: string
        address1?: string; address2?: string; city?: string; zip?: string
        countryCode?: string; provinceCode?: string; phone?: string
    } | null
    billingAddress?: {
        firstName?: string; lastName?: string; company?: string
        address1?: string; address2?: string; city?: string; zip?: string
        countryCode?: string; provinceCode?: string; phone?: string
    } | null
    email?: string
    phone?: string
    shippingLine?: { title: string; price: number } | null
    appliedDiscount?: {
        title?: string; description?: string
        value: number; valueType: 'FIXED_AMOUNT' | 'PERCENTAGE'
    } | null
    discountCodes?: string[]
    acceptAutomaticDiscounts?: boolean
    taxExempt?: boolean
    useCustomerDefaultAddress?: boolean
    purchasingEntity?: { purchasingCompany: { companyId: string; companyLocationId: string; companyContactId: string } } | null
}

export async function createDraftOrder(
    input: DraftOrderCreateInput
): Promise<{ id: string; legacyResourceId: string; name: string }> {
    const mutationInput: any = { lineItems: input.lineItems }

    if (input.customerId) mutationInput.customerId = input.customerId
    if (input.email) mutationInput.email = input.email
    if (input.phone) mutationInput.phone = input.phone
    if (input.shippingAddress) mutationInput.shippingAddress = input.shippingAddress
    if (input.billingAddress) mutationInput.billingAddress = input.billingAddress
    if (input.shippingLine) mutationInput.shippingLine = input.shippingLine
    if (input.appliedDiscount) mutationInput.appliedDiscount = input.appliedDiscount
    if (input.discountCodes?.length) mutationInput.discountCodes = input.discountCodes
    if (input.acceptAutomaticDiscounts) mutationInput.acceptAutomaticDiscounts = true
    if (input.taxExempt !== undefined) mutationInput.taxExempt = input.taxExempt
    if (input.useCustomerDefaultAddress) mutationInput.useCustomerDefaultAddress = true
    if (input.purchasingEntity) mutationInput.purchasingEntity = input.purchasingEntity

    const result = await shopifyQuery(DRAFT_ORDER_CREATE_MUTATION, { input: mutationInput })
    if (result.errors) {
        throw new Error(result.errors.map((e: any) => e.message).join(', '))
    }
    const userErrors = result.data?.draftOrderCreate?.userErrors
    if (userErrors?.length) {
        throw new Error(userErrors.map((e: any) => `${e.field?.join('.')}: ${e.message}`).join(', '))
    }
    return result.data.draftOrderCreate.draftOrder
}

export function parseMetafield(metafield: { value: string } | null): PaymentMetafield | null {
    if (!metafield?.value) return null
    try {
        return JSON.parse(metafield.value)
    } catch {
        return null
    }
}
